import Link from "next/link";
import type { ProductCard as ProductCardData } from "@/lib/shopify/types";
import { ProductImage } from "@/components/ui/ProductImage";
import { Price } from "./Price";

type Props = { product: ProductCardData; sizes?: string; preload?: boolean };

export function ProductCard({
  product,
  sizes = "(min-width: 1024px) 25vw, (min-width: 640px) 33vw, 50vw",
  preload,
}: Props) {
  return (
    <article className="group group/card relative">
      <ProductImage
        image={product.featuredImage}
        alt={product.title}
        sizes={sizes}
        preload={preload}
        className="aspect-[4/5] w-full rounded-[4px]"
      />
      <h3 className="mt-4 font-display text-xl leading-snug">
        <Link href={`/products/${product.handle}`} className="group-hover:underline group-hover:underline-offset-4">
          {/* Stretches the link over the whole card. */}
          <span className="absolute inset-0" aria-hidden />
          {product.title}
        </Link>
      </h3>
      <p className="mt-1 text-[0.9375rem] text-paper">
        {product.availableForSale ? (
          <Price price={product.priceRange.min} maxPrice={product.priceRange.max} compareAtPrice={product.compareAtPrice} />
        ) : (
          "Sold out"
        )}
      </p>
    </article>
  );
}

export function ProductGrid({ products, preloadFirst = false }: { products: ProductCardData[]; preloadFirst?: boolean }) {
  return (
    <ul className="grid grid-cols-2 gap-x-5 gap-y-10 sm:grid-cols-3 lg:grid-cols-4 lg:gap-x-8">
      {products.map((product, index) => (
        <li key={product.id} className="reveal" style={{ ["--step" as string]: index % 4 }}>
          <ProductCard product={product} preload={preloadFirst && index < 2} />
        </li>
      ))}
    </ul>
  );
}
